import React, { useState, useRef, lazy } from 'react';
import { Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, Lock, Gift, MapPin, ChevronLeft, Bell, BellOff, CheckCircle, Flame, ArrowLeft, ShoppingBag, Image as ImageIcon, Mic, Flag, Target, Music, Award, DollarSign, Send, ChevronRight, Video, Sparkles, X } from 'lucide-react';
import { VideoGenerationUpsell } from './VideoGenerationUpsell';
interface BonnyBlueAngelFeedProps {
  onBack: () => void;
}
export const BonnyBlueAngelFeed: React.FC<BonnyBlueAngelFeedProps> = ({
  onBack
}) => {
  const [activeTab, setActiveTab] = useState('all');
  const [isFollowing, setIsFollowing] = useState(false);
  const [likedPosts, setLikedPosts] = useState<Record<string, boolean>>({});
  const [showNotificationOptions, setShowNotificationOptions] = useState(false);
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [showPollResults, setShowPollResults] = useState(false);
  const [pollVote, setPollVote] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockPostId, setUnlockPostId] = useState<string | null>(null);
  const [showImageGeneratorModal, setShowImageGeneratorModal] = useState(false);
  const [showVideoUpsellModal, setShowVideoUpsellModal] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);
  const handleBackClick = () => {
    // Check if we should return to all angels grid
    const previousSection = localStorage.getItem('previousSection');
    const returnToAllAngels = window.sessionStorage.getItem('returnToAllAngels');
    if (previousSection === 'all_angels' || returnToAllAngels === 'true') {
      console.log('Navigating back to all angels grid');
      // Clear the flag
      window.sessionStorage.removeItem('returnToAllAngels');
      // Use the global navigation function if available
      if (typeof window.navigateBackToAllAngels === 'function') {
        window.navigateBackToAllAngels();
      } else {
        // Fallback navigation
        const navigateEvent = new CustomEvent('navigate', {
          detail: {
            section: 'all_angels'
          }
        });
        document.dispatchEvent(navigateEvent);
        // Also dispatch a specific event for AllAngelsGrid
        const backEvent = new CustomEvent('navigateBackToAllAngels');
        document.dispatchEvent(backEvent);
      }
    } else {
      // Fall back to the general discovery feed
      const navigateEvent = new CustomEvent('navigateBackToDiscovery');
      document.dispatchEvent(navigateEvent);
    }
    // Call the original onBack function
    onBack();
  };
  const tabs = [{
    id: 'all',
    label: 'All',
    icon: <Flame size={14} data-id="element-2194" />
  }, {
    id: 'media',
    label: 'Media',
    icon: <ImageIcon size={14} data-id="element-2195" />
  }, {
    id: 'voice',
    label: 'Voice',
    icon: <Mic size={14} data-id="element-2196" />
  }, {
    id: 'missions',
    label: 'Missions',
    icon: <Target size={14} data-id="element-2197" />
  }, {
    id: 'moans',
    label: 'Moans',
    icon: <Music size={14} data-id="element-2198" />
  }, {
    id: 'polls',
    label: 'Polls',
    icon: <Flag size={14} data-id="element-2199" />
  }, {
    id: 'upsells',
    label: 'Upsells',
    icon: <Lock size={14} data-id="element-2200" />
  }, {
    id: 'rewards',
    label: 'Rewards',
    icon: <Award size={14} data-id="element-2201" />
  }, {
    id: 'shop',
    label: 'Shop',
    icon: <ShoppingBag size={14} data-id="element-2202" />
  }];
  const scrollTabs = (direction: 'left' | 'right') => {
    if (tabsRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      tabsRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  const toggleFollow = () => {
    setIsFollowing(!isFollowing);
  };
  const toggleNotification = () => {
    setNotificationEnabled(!notificationEnabled);
    setShowNotificationOptions(false);
  };
  const toggleLike = (postId: string) => {
    setLikedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };
  const handlePollVote = (option: string) => {
    setPollVote(option);
    setShowPollResults(true);
  };
  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would submit the comment
    setCommentText('');
  };
  const handleUnlock = (postId: string) => {
    setUnlockPostId(postId);
    setShowUnlockModal(true);
  };
  const confirmUnlock = () => {
    // Here you would handle the actual token transaction
    setShowUnlockModal(false);
  };
  const posts = [{
    id: 'post1',
    type: 'premium_content',
    content: "Ready to play a game? I'll be your dominant player tonight... unlock to see what I have in store for you 😈",
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jq4Dkth7vgkmZJ8GZmzWDP/bonnyblue.png',
    likes: 3452,
    comments: 287,
    time: '2h ago',
    locked: true,
    price: 10,
    location: 'My Gaming Room',
    hashtags: ['#gamer', '#dominant', '#playtime', '#exclusive']
  }, {
    id: 'post2',
    type: 'poll',
    content: 'What game should we play together tonight? The winner gets a special private stream... 🎮',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jq4Dkth7vgkmZJ8GZmzWDP/bonnyblue.png',
    likes: 2789,
    comments: 412,
    time: '5h ago',
    location: 'Stream Setup',
    poll: {
      options: [{
        text: 'Roleplay adventure 🧙‍♀️',
        percentage: 65
      }, {
        text: 'Competitive action 🔫',
        percentage: 35
      }],
      endsIn: '3 hours'
    },
    hashtags: ['#gaming', '#stream', '#poll', '#gamerlife']
  }, {
    id: 'post3',
    type: 'voice_note',
    content: 'Listen to what I whisper during intense gaming moments... I get quite passionate 🎧',
    likes: 4231,
    comments: 512,
    time: '1d ago',
    location: 'Late Night Stream',
    audioWaveform: [0.2, 0.5, 0.3, 0.8, 0.4, 0.6, 0.3, 0.5, 0.7, 0.8, 0.5, 0.3, 0.6, 0.4, 0.2],
    duration: '0:58',
    hashtags: ['#voice', '#gaming', '#asmr', '#passionate']
  }];
  // Filter posts based on active tab
  const filteredPosts = posts.filter(post => {
    if (activeTab === 'all') return true;
    if (activeTab === 'media' && (post.type === 'image' || post.type === 'premium_content')) return true;
    if (activeTab === 'voice' && post.type === 'voice_note') return true;
    if (activeTab === 'polls' && post.type === 'poll') return true;
    if (activeTab === 'upsells' && post.type === 'premium_content') return true;
    return false;
  });
  return <section className="w-full max-w-[860px] mx-auto px-4 space-y-4 angel-feed-container" data-id="element-2203">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gray-950/90 backdrop-blur-sm p-3 flex items-center justify-between border-b border-gray-800" data-id="element-2204">
        <div className="flex items-center gap-4" data-id="element-2205">
          <button className="p-2 rounded-full hover:bg-gray-800" onClick={handleBackClick} data-id="element-2206">
            <ArrowLeft size={20} data-id="element-2207" />
          </button>
          <div data-id="element-2208">
            <h1 className="font-bold text-lg" data-id="element-2209">Bonny Blue</h1>
            <p className="text-xs text-gray-400" data-id="element-2210">980K Followers</p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-2211">
          <button className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 rounded-full text-white text-xs font-medium" onClick={() => setShowImageGeneratorModal(true)} data-id="element-2212">
            <ImageIcon size={12} className="text-blue-400" data-id="element-2213" />
            <span data-id="element-2214">Generate Image</span>
          </button>
          <button className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 rounded-full text-white text-xs font-medium" onClick={() => setShowVideoUpsellModal(true)} data-id="element-2215">
            <Video size={12} className="text-blue-400" data-id="element-2216" />
            <span data-id="element-2217">Generate Video</span>
          </button>
          <div className="relative" data-id="element-2218">
            <button className="p-2 bg-gray-800 rounded-full hover:bg-gray-700" onClick={() => setShowNotificationOptions(!showNotificationOptions)} data-id="element-2219">
              {notificationEnabled ? <Bell size={20} data-id="element-2220" /> : <BellOff size={20} data-id="element-2221" />}
            </button>
            {showNotificationOptions && <div className="absolute right-0 mt-2 w-48 bg-gray-900 rounded-lg shadow-lg border border-gray-800 z-10" data-id="element-2222">
                <button className="w-full px-4 py-2 text-left hover:bg-gray-800 rounded-t-lg flex items-center gap-2" onClick={toggleNotification} data-id="element-2223">
                  {notificationEnabled ? <>
                      <BellOff size={16} data-id="element-2224" />
                      <span data-id="element-2225">Disable notifications</span>
                    </> : <>
                      <Bell size={16} data-id="element-2226" />
                      <span data-id="element-2227">Enable notifications</span>
                    </>}
                </button>
              </div>}
          </div>
          <button className={`px-4 py-1.5 rounded-full font-bold ${isFollowing ? 'bg-gray-800 text-white hover:bg-red-600 hover:text-white' : 'bg-gradient-to-r from-purple-500 to-blue-600 text-white hover:opacity-90'}`} onClick={toggleFollow} data-id="element-2228">
            {isFollowing ? 'Following' : 'Follow'}
          </button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-800 hover:bg-gray-700 transition-colors" data-id="element-2229">
            <Sparkles size={16} className="text-blue-400" data-id="element-2230" />
          </button>
        </div>
      </div>

      {/* Profile Header */}
      <div className="relative" data-id="element-2231">
        <div className="h-32 w-full bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-xl" data-id="element-2232"></div>
        <div className="absolute -bottom-16 left-4 w-32 h-32 rounded-full border-4 border-gray-950 overflow-hidden" data-id="element-2233">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jq4Dkth7vgkmZJ8GZmzWDP/bonnyblue.png" alt="Bonny Blue" className="w-full h-full object-cover image-high-quality" loading="lazy" data-id="element-2234" />
        </div>
      </div>

      {/* Profile Info */}
      <div className="mt-20 px-4 ml-36" data-id="element-2235">
        <div className="flex items-center gap-1" data-id="element-2236">
          <h2 className="text-xl font-bold" data-id="element-2237">Bonny Blue</h2>
          <CheckCircle size={16} className="text-blue-400" fill="currentColor" data-id="element-2238" />
          <span className="text-sm font-normal text-blue-400 ml-2" data-id="element-2239">💙💋</span>
        </div>
        <p className="text-gray-400" data-id="element-2240">@BonnyBlueOfficial</p>
        <p className="mt-2" data-id="element-2241">
          Dominant & playful gamer girl. I'll show you who's boss in the virtual
          world and beyond... 😈
        </p>
        <div className="flex gap-4 mt-3 text-sm" data-id="element-2242">
          <div className="flex items-center gap-1" data-id="element-2243">
            <span className="font-bold" data-id="element-2244">132</span>
            <span className="text-gray-400" data-id="element-2245">Following</span>
          </div>
          <div className="flex items-center gap-1" data-id="element-2246">
            <span className="font-bold" data-id="element-2247">980K</span>
            <span className="text-gray-400" data-id="element-2248">Followers</span>
          </div>
          <div className="flex items-center gap-1" data-id="element-2249">
            <span className="font-bold" data-id="element-2250">480</span>
            <span className="text-gray-400" data-id="element-2251">Intimacy XP</span>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mt-4 relative border-b border-gray-800" data-id="element-2252">
        <button className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-gray-900/80 rounded-full text-gray-400 hover:text-white" onClick={() => scrollTabs('left')} data-id="element-2253">
          <ChevronLeft size={18} data-id="element-2254" />
        </button>
        <div ref={tabsRef} className="flex overflow-x-auto scrollbar-hide px-8 py-1" data-id="element-2255">
          {tabs.map(tab => <button key={tab.id} className={`px-4 py-2 flex items-center gap-1.5 whitespace-nowrap rounded-full mx-1 ${activeTab === tab.id ? 'bg-gradient-to-r from-purple-500 to-blue-600 text-white' : 'bg-gray-800/50 text-gray-300 hover:bg-gray-800'}`} onClick={() => setActiveTab(tab.id)} data-id="element-2256">
              {tab.icon}
              <span data-id="element-2257">{tab.label}</span>
            </button>)}
        </div>
        <button className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-gray-900/80 rounded-full text-gray-400 hover:text-white" onClick={() => scrollTabs('right')} data-id="element-2258">
          <ChevronRight size={18} data-id="element-2259" />
        </button>
      </div>

      {/* Posts Feed */}
      <div className="space-y-4" data-id="element-2260">
        {filteredPosts.length > 0 ? filteredPosts.map(post => <div key={post.id} className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden animate-in fade-in duration-300 angel-feed-post shadow-md hover:shadow-xl hover:shadow-purple-600/5 transition-all duration-300" data-id="element-2261">
              {/* Post Header */}
              <div className="p-3 flex items-center gap-2 border-b border-gray-800" data-id="element-2262">
                <div className="w-10 h-10 rounded-full overflow-hidden" data-id="element-2263">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jq4Dkth7vgkmZJ8GZmzWDP/bonnyblue.png" alt="Bonny Blue" className="w-full h-full object-cover image-high-quality" loading="lazy" data-id="element-2264" />
                </div>
                <div data-id="element-2265">
                  <div className="flex items-center gap-1" data-id="element-2266">
                    <h3 className="font-medium" data-id="element-2267">Bonny Blue</h3>
                    <CheckCircle size={14} className="text-blue-400" fill="currentColor" data-id="element-2268" />
                    <span className="text-xs text-gray-400" data-id="element-2269">• {post.time}</span>
                  </div>
                  {post.location && <div className="flex items-center gap-1 text-xs text-gray-400" data-id="element-2270">
                      <MapPin size={10} className="ml-1" data-id="element-2271" />
                      <span data-id="element-2272">{post.location}</span>
                    </div>}
                </div>
                <button className="ml-auto" data-id="element-2273">
                  <MoreHorizontal size={16} className="text-gray-400" data-id="element-2274" />
                </button>
              </div>

              {/* Post Content */}
              <div className="px-3 py-2" data-id="element-2275">
                <p className="text-sm" data-id="element-2276">{post.content}</p>
                {/* Hashtags */}
                {post.hashtags && <div className="text-xs text-purple-400 mt-1" data-id="element-2277">
                    {post.hashtags.join(' ')}
                  </div>}
              </div>

              {/* Post Media */}
              {(post.type === 'image' || post.type === 'poll' && post.image) && post.image && <div className="aspect-square bg-gray-800" data-id="element-2278">
                    <img src={post.image} alt="Post" className="w-full h-full object-cover angel-post-image hover:scale-[1.02] transition-transform duration-300" sizes="(max-width: 768px) 100vw, 820px" loading="lazy" data-id="element-2279" />
                  </div>}

              {/* Premium Content */}
              {post.type === 'premium_content' && post.image && <div className="relative aspect-square bg-gray-800" data-id="element-2280">
                  <img src={post.image} alt="Locked Content" className="w-full h-full object-cover blur-sm angel-post-image" sizes="(max-width: 768px) 100vw, 820px" loading="lazy" data-id="element-2281" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-2282">
                    <Lock size={32} className="text-purple-400 mb-2" data-id="element-2283" />
                    <p className="text-center text-white font-medium" data-id="element-2284">
                      Unlock Exclusive Photo
                    </p>
                    <button className="mt-3 px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-600 rounded-full text-white font-medium flex items-center gap-2 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300" onClick={() => handleUnlock(post.id)} data-id="element-2285">
                      <span data-id="element-2286">Unlock for</span>
                      <span className="text-purple-200" data-id="element-2287">{post.price} 💎</span>
                    </button>
                  </div>
                </div>}

              {/* Poll */}
              {post.type === 'poll' && post.poll && <div className="p-3" data-id="element-2288">
                  <div className="space-y-2" data-id="element-2289">
                    {post.poll.options.map((option, i) => <button key={i} className={`w-full p-3 bg-gray-800 rounded-lg border ${pollVote === option.text ? 'border-purple-500' : 'border-gray-700 hover:border-purple-500'} transition flex justify-between items-center`} onClick={() => handlePollVote(option.text)} data-id="element-2290">
                        <span data-id="element-2291">{option.text}</span>
                        {(showPollResults || pollVote) && <span className="text-xs bg-gray-700 px-2 py-1 rounded-full" data-id="element-2292">
                            {option.percentage}%
                          </span>}
                      </button>)}
                    <p className="text-xs text-gray-400 mt-2" data-id="element-2293">
                      Voting ends in {post.poll.endsIn} • Results will shape
                      tonight's stream
                    </p>
                  </div>
                </div>}

              {/* Voice Note */}
              {post.type === 'voice_note' && post.audioWaveform && <div className="p-3" data-id="element-2294">
                  <div className="p-3 bg-gray-800 rounded-lg border border-gray-700" data-id="element-2295">
                    <div className="flex items-center gap-3" data-id="element-2296">
                      <button className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-r from-purple-500 to-blue-600 text-white hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300" data-id="element-2297">
                        <div className="ml-1" data-id="element-2298">▶</div>
                      </button>
                      <div className="flex-grow h-10 flex items-center" data-id="element-2299">
                        {post.audioWaveform.map((level, i) => <div key={i} className="w-1 mx-0.5 rounded-full bg-gradient-to-t from-purple-500/50 to-blue-500/50" style={{
                  height: `${level * 32}px`
                }} data-id="element-2300"></div>)}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-400" data-id="element-2301">
                        <span data-id="element-2302">{post.duration}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2" data-id="element-2303">
                      🎧 Best experienced with headphones
                    </p>
                  </div>
                </div>}

              {/* Post Actions */}
              <div className="p-3" data-id="element-2304">
                <div className="flex items-center justify-between" data-id="element-2305">
                  <div className="flex items-center gap-3" data-id="element-2306">
                    <button className="flex items-center gap-1 group" onClick={() => toggleLike(post.id)} data-id="element-2307">
                      <Heart size={18} className={likedPosts[post.id] ? 'text-purple-500' : 'text-gray-400 group-hover:text-purple-400'} fill={likedPosts[post.id] ? 'currentColor' : 'none'} data-id="element-2308" />
                      <span className="text-xs" data-id="element-2309">
                        {likedPosts[post.id] ? post.likes + 1 : post.likes}
                      </span>
                    </button>
                    <button className="flex items-center gap-1" data-id="element-2310">
                      <MessageCircle size={18} className="text-gray-400" data-id="element-2311" />
                      <span className="text-xs" data-id="element-2312">{post.comments}</span>
                    </button>
                    <button className="flex items-center gap-1" data-id="element-2313">
                      <Share2 size={18} className="text-gray-400" data-id="element-2314" />
                      <span className="text-xs" data-id="element-2315">Share</span>
                    </button>
                  </div>
                  <button className="flex items-center gap-1" data-id="element-2316">
                    <Gift size={18} className="text-purple-400" data-id="element-2317" />
                    <span className="text-xs" data-id="element-2318">Gift</span>
                  </button>
                </div>
                <div className="flex items-center gap-1 mt-2" data-id="element-2319">
                  <div className="flex -space-x-2" data-id="element-2320">
                    <div className="w-5 h-5 rounded-full border border-gray-900 bg-gray-700" data-id="element-2321"></div>
                    <div className="w-5 h-5 rounded-full border border-gray-900 bg-gray-700" data-id="element-2322"></div>
                  </div>
                  <span className="text-xs text-gray-400" data-id="element-2323">
                    Liked by Alex and {(post.likes - 1).toLocaleString()} others
                  </span>
                </div>
                <div className="text-xs text-gray-400 mt-1" data-id="element-2324">
                  View all {post.comments} comments
                </div>
              </div>
            </div>) : <div className="bg-gray-900 rounded-xl border border-gray-800 p-8 text-center" data-id="element-2325">
            <div className="w-16 h-16 mx-auto flex items-center justify-center bg-gray-800 rounded-full mb-4" data-id="element-2326">
              {activeTab === 'media' ? <ImageIcon size={24} className="text-gray-600" data-id="element-2327" /> : activeTab === 'voice' ? <Mic size={24} className="text-gray-600" data-id="element-2328" /> : <Heart size={24} className="text-gray-600" data-id="element-2329" />}
            </div>
            <h3 className="text-lg font-bold mb-2" data-id="element-2330">
              No {activeTab} content yet
            </h3>
            <p className="text-gray-400 max-w-md mx-auto" data-id="element-2331">
              Bonny Blue hasn't posted any {activeTab} content yet. Check back
              soon!
            </p>
          </div>}
      </div>

      {/* Mission Card */}
      <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-xl border border-purple-800/50 p-4 shadow-md hover:shadow-xl hover:shadow-purple-600/5 transition-all duration-300" data-id="element-2332">
        <div className="flex items-center gap-3" data-id="element-2333">
          <div className="w-10 h-10 flex items-center justify-center bg-purple-500/20 rounded-full text-purple-400" data-id="element-2334">
            🔥
          </div>
          <div data-id="element-2335">
            <h3 className="font-medium" data-id="element-2336">Daily Mission</h3>
            <p className="text-sm text-gray-300" data-id="element-2337">
              Watch Bonny's stream for 10 minutes
            </p>
          </div>
        </div>
        <div className="mt-3" data-id="element-2338">
          <div className="flex items-center justify-between mb-1" data-id="element-2339">
            <span className="text-xs text-gray-400" data-id="element-2340">Progress</span>
            <span className="text-xs text-gray-400" data-id="element-2341">
              {Object.values(likedPosts).filter(Boolean).length}/3
            </span>
          </div>
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden" data-id="element-2342">
            <div className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full" style={{
            width: `${Math.min(Object.values(likedPosts).filter(Boolean).length / 3 * 100, 100)}%`
          }} data-id="element-2343"></div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3" data-id="element-2344">
          <div className="flex items-center gap-1" data-id="element-2345">
            <span className="text-purple-400" data-id="element-2346">💜</span>
            <span className="text-sm" data-id="element-2347">+25</span>
          </div>
          <button className="text-xs px-3 py-1.5 bg-gradient-to-r from-purple-500 to-blue-600 rounded-full text-white font-medium hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300" data-id="element-2348">
            Join Stream
          </button>
        </div>
      </div>

      {/* Unlock Modal */}
      {showUnlockModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-2349">
          <div className="w-full max-w-sm bg-gray-900 rounded-xl border border-gray-800 overflow-hidden animate-in zoom-in-90 duration-300 shadow-2xl" data-id="element-2350">
            <div className="p-4 border-b border-gray-800" data-id="element-2351">
              <h3 className="font-bold" data-id="element-2352">Unlock Premium Content</h3>
            </div>
            <div className="p-4" data-id="element-2353">
              <p className="text-sm text-gray-300 mb-4" data-id="element-2354">
                Unlock this exclusive content from Bonny Blue for 10 gems. This
                is a one-time purchase.
              </p>
              <div className="flex items-center justify-center gap-2 mb-4" data-id="element-2355">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-blue-600 flex items-center justify-center text-2xl" data-id="element-2356">
                  💎
                </div>
              </div>
              <div className="flex items-center justify-between mb-4" data-id="element-2357">
                <span className="text-sm" data-id="element-2358">Your balance:</span>
                <span className="font-bold" data-id="element-2359">15 💎</span>
              </div>
              <div className="flex items-center justify-between mb-4" data-id="element-2360">
                <span className="text-sm" data-id="element-2361">Cost:</span>
                <span className="font-bold text-purple-400" data-id="element-2362">-10 💎</span>
              </div>
              <div className="flex gap-2" data-id="element-2363">
                <button className="flex-1 py-2 bg-gray-800 rounded-lg text-sm hover:bg-gray-700 transition-colors" onClick={() => setShowUnlockModal(false)} data-id="element-2364">
                  Cancel
                </button>
                <button className="flex-1 py-2 bg-gradient-to-r from-purple-500 to-blue-600 rounded-lg text-white text-sm font-medium hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300" onClick={confirmUnlock} data-id="element-2365">
                  Unlock Now
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Image Generator Modal */}
      {showImageGeneratorModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-2366">
          <div className="relative w-full max-w-lg animate-in zoom-in-90 duration-300 bg-white rounded-lg shadow-lg" data-id="element-2367">
            <button className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100" onClick={() => setShowImageGeneratorModal(false)} data-id="element-2368">
              <X size={18} className="text-gray-500" data-id="element-2369" />
            </button>
            <div className="p-6" data-id="element-2370">
              <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-2371">
                Generate Image with Bonny
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-2372">
                Create a custom image with Bonny. Choose your options and
                generate a unique image.
              </p>
              <div className="space-y-4" data-id="element-2373">
                <div data-id="element-2374">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2375">
                    Style
                  </label>
                  <input type="text" placeholder="Streetwear" className="w-full p-2 border border-gray-300 rounded-md text-gray-800" data-id="element-2376" />
                </div>
                <div data-id="element-2377">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2378">
                    Mood
                  </label>
                  <input type="text" placeholder="Playful" className="w-full p-2 border border-gray-300 rounded-md text-gray-800" data-id="element-2379" />
                </div>
                <div data-id="element-2380">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2381">
                    Special Request
                  </label>
                  <textarea placeholder="Describe what you'd like to see..." className="w-full p-2 border border-gray-300 rounded-md text-gray-800 min-h-[100px]" data-id="element-2382"></textarea>
                </div>
                <div data-id="element-2383">
                  <div className="flex items-center justify-between mb-2" data-id="element-2384">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-2385">
                      Cost:
                    </label>
                    <span className="text-pink-500 font-medium flex items-center gap-1" data-id="element-2386">
                      15 <span className="text-blue-500" data-id="element-2387">💎</span>
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3" data-id="element-2388">
                    <button className="py-2 px-4 bg-gray-100 rounded-md text-gray-800 hover:bg-gray-200 transition-colors" onClick={() => setShowImageGeneratorModal(false)} data-id="element-2389">
                      Cancel
                    </button>
                    <button className="py-2 px-4 bg-pink-600 rounded-md text-white font-medium hover:bg-pink-700 transition-colors" data-id="element-2390">
                      Generate Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Video Generation Modal */}
      {showVideoUpsellModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-2391">
          <VideoGenerationUpsell onClose={() => setShowVideoUpsellModal(false)} onPurchase={(duration, tokens) => {
        // Handle purchase logic
        setShowVideoUpsellModal(false);
      }} data-id="element-2392" />
        </div>}

      <style jsx data-id="element-2393">{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .angel-feed-container {
          max-width: 860px;
          margin: 0 auto;
          padding: 0 16px;
        }
        @media (min-width: 1440px) {
          .angel-feed-container {
            max-width: 1080px;
          }
        }
        .angel-feed-post {
          max-width: 820px;
          margin: 0 auto;
        }
        .angel-post-image {
          width: 100%;
          height: auto;
          object-fit: cover;
          image-rendering: auto;
          border-radius: 0;
        }
        .image-high-quality {
          image-rendering: auto;
        }
      `}</style>
    </section>;
};